import React, { useState } from "react";
import { Alert, Button, Spin, Tooltip, Typography } from "antd";
import { MoonOutlined, SunOutlined } from "@ant-design/icons";
import { GoogleLogin } from "@react-oauth/google";
import { useNavigate } from "react-router-dom";
import styles from "./styles.module.scss";
import { useAuth, NO_ACCESS_MESSAGE } from "../../../hooks/useAuth";
import { useTheme } from "../../../theme/ThemeContext";
import { environment } from "../../../configuration";
import emmiLogo from "../../../assets/emmi_logo_auth.png";

const { Title, Text } = Typography;

const GOOGLE_FAILURE_MESSAGE =
  "No se pudo completar el inicio de sesión con Google. Intenta nuevamente.";

const Login = () => {
  const { loginWithGoogle, isLoading } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  const themeTooltip = theme === "dark" ? "Modo claro" : "Modo oscuro";
  const isGoogleConfigured = !!environment.googleClientId;

  const onGoogleSuccess = async (credential?: string) => {
    if (!credential) {
      setError(GOOGLE_FAILURE_MESSAGE);
      return;
    }

    setError(null);
    const result = await loginWithGoogle(credential);

    if (result.success) {
      return navigate("/", { replace: true });
    }

    setError(result.message || NO_ACCESS_MESSAGE);
  };

  return (
    <div className={styles.container}>
      <div className={styles.card}>
        {/* Same affordance as the app Header toggle so the login page is
            switchable before authenticating. */}
        <Tooltip title={themeTooltip}>
          <Button
            type="text"
            aria-label={themeTooltip}
            icon={theme === "dark" ? <SunOutlined /> : <MoonOutlined />}
            onClick={toggleTheme}
            className={styles.themeToggle}
          />
        </Tooltip>
        <div className={styles.brand}>
          <img src={emmiLogo} alt="Emmi Logo" className={styles.logo} />
          <Title level={3} className={styles.title}>
            Emmi Admin
          </Title>
          <Text type="secondary">Inicia sesión con tu cuenta de Google</Text>
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            message={error}
            className={styles.error}
            role="alert"
          />
        )}

        <div className={styles.googleButton}>
          {!isGoogleConfigured ? (
            // Never render a dead Google button: without a client id the GIS
            // script cannot mint a token, and the failure would look like a
            // rejected login instead of a missing deploy variable.
            <Alert
              type="warning"
              showIcon
              message="El inicio de sesión con Google no está configurado en este entorno."
            />
          ) : isLoading ? (
            <Spin />
          ) : (
            <GoogleLogin
              onSuccess={(credentialResponse) =>
                onGoogleSuccess(credentialResponse.credential)
              }
              onError={() => setError(GOOGLE_FAILURE_MESSAGE)}
              // Renders Google's own "Continuar con Google" wording; the
              // language comes from GoogleOAuthProvider's `locale`.
              text="continue_with"
              width="336"
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
